import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  getActiveTheme,
  resolveThemeForScreen,
  setActiveTheme,
  subscribeActiveTheme,
  type ThemeMode,
} from '@beatq/shared';

export type { ThemeMode };
export { resolveThemeForScreen };

const ThemeContext = createContext<ThemeMode>('standard');
const SetThemeContext = createContext<(theme: ThemeMode) => void>(setActiveTheme);
/** False at the app root; true inside a ThemeProvider so demos cannot claim html. */
const ThemeRootContext = createContext(false);

export interface ThemeProviderProps {
  /** Initial theme only — ScreenFrame / setActiveTheme drive the live value. */
  theme?: ThemeMode;
  children: ReactNode;
}

export function ThemeProvider({ theme: initialTheme = 'standard', children }: ThemeProviderProps) {
  const isNested = useContext(ThemeRootContext);
  const [theme, setThemeState] = useState<ThemeMode>(() =>
    isNested ? initialTheme : getActiveTheme(),
  );

  useEffect(() => {
    if (isNested) {
      setThemeState(initialTheme);
      return;
    }
    // Seed once from the app root default when nothing has claimed a screen yet.
    if (getActiveTheme() === 'standard') {
      setActiveTheme(initialTheme);
    }
    setThemeState(getActiveTheme());
    return subscribeActiveTheme(() => setThemeState(getActiveTheme()));
  }, [initialTheme, isNested]);

  const setTheme = useCallback(
    (next: ThemeMode) => {
      if (isNested) {
        setThemeState(next);
        return;
      }
      setActiveTheme(next);
    },
    [isNested],
  );

  const resolved = isNested ? initialTheme : theme;

  return (
    <ThemeRootContext.Provider value={true}>
      <SetThemeContext.Provider value={setTheme}>
        <ThemeContext.Provider value={resolved}>
          <div data-theme={resolved}>{children}</div>
        </ThemeContext.Provider>
      </SetThemeContext.Provider>
    </ThemeRootContext.Provider>
  );
}

export function useThemeMode(): ThemeMode {
  return useContext(ThemeContext);
}

/** Updates the app-wide theme (html + ThemeProvider wrapper). */
export function useSetTheme(): (theme: ThemeMode) => void {
  return useContext(SetThemeContext);
}

/**
 * Route-level theme boundary. Declares the theme for this screen subtree
 * via the shared active-theme store (07-11: theme is per route, not user-selected).
 */
export function ScreenTheme({ screenId, children }: { screenId: string; children: ReactNode }) {
  const theme = resolveThemeForScreen(screenId);
  useEffect(() => {
    setActiveTheme(theme);
  }, [theme]);
  return <>{children}</>;
}
